import { Play } from "@/queries/plays";
import { useNotifications } from "@/stores/NotificationsContext";
import { useEffect, useState } from "react";

const NOTIFIED_PLAYS_STORAGE_KEY = "notifiedPlays";

export const useScheduledPlayNotifications = (
  plays: Play[],
  checkIntervalMs = 60000 // Check every minute by default
) => {
  const { addNotification } = useNotifications();

  // Initialize state from localStorage to persist across refreshes
  const [notifiedPlayIds, setNotifiedPlayIds] = useState<number[]>(() => {
    try {
      const storedData = localStorage.getItem(NOTIFIED_PLAYS_STORAGE_KEY);
      return storedData ? JSON.parse(storedData) : [];
    } catch (err) {
      console.error("Error loading notified plays from localStorage:", err);
      return [];
    }
  });

  // Save notifiedPlayIds to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(
      NOTIFIED_PLAYS_STORAGE_KEY,
      JSON.stringify(notifiedPlayIds)
    );
  }, [notifiedPlayIds]);

  // Function to check for plays about to start
  const checkScheduledPlays = () => {
    const now = new Date();

    plays.forEach((play) => {
      if (!play.override_scheduled_at || notifiedPlayIds.includes(play.id)) {
        return; // Skip if no schedule or already notified
      }

      const scheduledTime = new Date(play.override_scheduled_at);
      const timeDifferenceMs = scheduledTime.getTime() - now.getTime();

      // If the play is scheduled within the next 5 minutes (300000ms)
      if (timeDifferenceMs > 0 && timeDifferenceMs <= 300000) {
        // Create a notification via the context which uses the API
        addNotification({
          play_id: play.id,
          title: "Scheduled Play Starting Soon",
          message: `"${play.title}" is scheduled to start in ${Math.floor(timeDifferenceMs / 60000)} minutes.`,
        }).catch((err) => {
          console.error("Failed to create notification:", err);
        });

        // Mark this play as notified locally so we don't notify again
        setNotifiedPlayIds((prev) => [...prev, play.id]);
      }
    });
  };

  // Set up interval to check scheduled plays
  useEffect(() => {
    // Check once when the hook is initialized
    checkScheduledPlays();

    // Set up interval for subsequent checks
    const intervalId = setInterval(checkScheduledPlays, checkIntervalMs);

    return () => {
      clearInterval(intervalId);
    };
  }, [plays, checkIntervalMs]); // Re-run effect if plays or interval changes
};
